import * as React from 'react'

import type { FieldSpec, RuleGroup, RuleNode } from '@/lib/types'
import { isGroup } from '@/lib/types'
import { defaultValueFor, preferredOperator } from './RuleTree'

/** Resolves the parent group and child index for a JSONPath like `$.root.children[2].children[0]`. */
export function resolveParent(
  root: RuleGroup,
  path: string,
): { parent: RuleGroup | null; index: number } {
  const indices = [...path.matchAll(/children\[(\d+)\]/g)].map((m) => Number(m[1]))
  if (indices.length === 0) return { parent: null, index: -1 }

  let current: RuleNode = root
  for (let i = 0; i < indices.length - 1; i++) {
    if (!isGroup(current)) return { parent: null, index: -1 }
    current = current.children[indices[i]]
    if (!current) return { parent: null, index: -1 }
  }
  if (!isGroup(current)) return { parent: null, index: -1 }
  return { parent: current, index: indices[indices.length - 1] }
}

export function resolveNode(root: RuleGroup, path: string): RuleNode | null {
  const { parent, index } = resolveParent(root, path)
  if (!parent || index < 0) return null
  return parent.children[index] ?? null
}

/**
 * The add/change/duplicate callbacks a RuleNodeEditor needs, over any root.
 *
 * Extracted so the tag rule builder and the rule editor manipulate a condition
 * tree through one implementation. Two copies of "delete the node at this
 * JSONPath" is two chances for one of them to splice the wrong index, and the
 * symptom — a condition silently vanishing from a saved rule — looks nothing
 * like its cause.
 */
export function useRuleTree(
  setRoot: (updater: (prev: RuleGroup) => RuleGroup) => void,
  fields: FieldSpec[],
) {
  const onChange = React.useCallback(
    (path: string, updater: (node: RuleNode) => RuleNode | null) => {
      setRoot((prev) => {
        const next = structuredClone(prev)
        if (path === '$.root') {
          const updated = updater(next)
          return updated && isGroup(updated) ? updated : next
        }
        const { parent, index } = resolveParent(next, path)
        if (!parent || index < 0) return next
        const updated = updater(parent.children[index])
        if (updated === null) parent.children.splice(index, 1)
        else parent.children[index] = updated
        return next
      })
    },
    [setRoot],
  )

  const onAddCondition = React.useCallback(
    (path: string, fieldName?: string) => {
      setRoot((prev) => {
        const next = structuredClone(prev)
        const group = path === '$.root' ? next : (resolveNode(next, path) as RuleGroup | null)
        if (!group || !isGroup(group)) return next
        const spec = fields.find((f) => f.name === fieldName) ?? fields.find((f) => !f.deferred)
        const operator = preferredOperator(spec)
        group.children.push({
          type: 'condition',
          field: spec?.name ?? 'label',
          operator,
          value: defaultValueFor(spec, operator),
          id: crypto.randomUUID(),
        })
        return next
      })
    },
    [fields, setRoot],
  )

  const onAddGroup = React.useCallback(
    (path: string) => {
      setRoot((prev) => {
        const next = structuredClone(prev)
        const group = path === '$.root' ? next : (resolveNode(next, path) as RuleGroup | null)
        if (!group || !isGroup(group)) return next
        group.children.push({
          type: 'group', op: 'OR', not: false, children: [], id: crypto.randomUUID(),
        })
        return next
      })
    },
    [setRoot],
  )

  const onDuplicate = React.useCallback(
    (path: string) => {
      setRoot((prev) => {
        const next = structuredClone(prev)
        const { parent, index } = resolveParent(next, path)
        if (!parent || index < 0) return next
        const duplicate = structuredClone(parent.children[index])
        duplicate.id = crypto.randomUUID()
        parent.children.splice(index + 1, 0, duplicate)
        return next
      })
    },
    [setRoot],
  )

  return { onChange, onAddCondition, onAddGroup, onDuplicate }
}
